import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FocalImage } from "@/components/ui/focal-image";
import { PlaceHolderImages } from "@/lib/placeholder-images";
import { toggleRecipeFavorite } from "@/lib/recipe-service";
import type { Recipe, RecipeBox } from "@/lib/types";
import { Star, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";

interface RecipeCardListProps {
    recipe: Recipe;
    userId?: string | null;
    recipeBox?: RecipeBox | null;
    onFavoriteToggle?: (isFavorite: boolean) => void;
}

const FALLBACK_RECIPE_IMAGE = PlaceHolderImages[0]?.imageUrl ?? "https://picsum.photos/seed/placeholder/600/400";
const FALLBACK_IMAGE_HINT = PlaceHolderImages[0]?.imageHint ?? "recipe placeholder";

/**
 * List view variant of RecipeCard - displays recipe in a horizontal list format
 */
export function RecipeCardList({ recipe, userId, recipeBox, onFavoriteToggle }: Readonly<RecipeCardListProps>) {
    const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
    const [optimisticFavorite, setOptimisticFavorite] = useState<boolean | null>(null);
    const imageUrl = recipe.imageUrl || FALLBACK_RECIPE_IMAGE;
    const imageHint = recipe.imageHint || FALLBACK_IMAGE_HINT;

    // Use optimistic state if available, otherwise fall back to prop
    // The optimistic state will be cleared after the async operation completes,
    // allowing Firestore subscription updates to take over
    const isFavorite = optimisticFavorite !== null ? optimisticFavorite : (recipe.isFavorite ?? false);

    const handleToggleFavorite = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!userId || isTogglingFavorite) {
            return;
        }

        // Optimistic update - update UI immediately
        const newFavoriteStatus = !isFavorite;
        setOptimisticFavorite(newFavoriteStatus);
        setIsTogglingFavorite(true);

        try {
            const confirmedFavoriteStatus = await toggleRecipeFavorite(recipe.id, userId, isFavorite, recipeBox);
            // Clear optimistic state - Firestore subscription will update the prop
            setOptimisticFavorite(null);
            onFavoriteToggle?.(confirmedFavoriteStatus);
        } catch (error) {
            console.error("Failed to toggle favorite", error);
            // Revert optimistic update on error - show original state
            setOptimisticFavorite(null);
        } finally {
            setIsTogglingFavorite(false);
        }
    };

    return (
        <Link to={`/recipe/${recipe.id}`} className="block h-full">
            <Card className="group relative flex h-24 flex-row overflow-hidden transition-all duration-200 ease-in-out hover:shadow-md">
                <div className="relative w-24 shrink-0 overflow-hidden">
                    <FocalImage
                        src={imageUrl}
                        alt={recipe.title}
                        className="absolute inset-0 h-full w-full"
                        data-ai-hint={imageHint}
                        focalPointX={recipe.imageFocalPointX}
                        focalPointY={recipe.imageFocalPointY}
                        zoom={recipe.imageZoom}
                    />
                </div>
                <CardContent className="flex flex-1 flex-col justify-center gap-1 p-2">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            <h3 className="font-headline text-sm font-semibold leading-snug truncate">{recipe.title}</h3>
                            {(recipe.prepTime || recipe.cookTime || recipe.servings) ? (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {[recipe.prepTime, recipe.cookTime, recipe.servings]
                                        .filter(Boolean)
                                        .join(" • ")}
                                </p>
                            ) : null}
                        </div>
                        {userId && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0"
                                onClick={handleToggleFavorite}
                                disabled={isTogglingFavorite}
                                aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                            >
                                {isTogglingFavorite ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Star className={`h-4 w-4 ${isFavorite ? "fill-yellow-500 text-yellow-500" : ""}`} />
                                )}
                            </Button>
                        )}
                    </div>
                    {recipe.labels.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                            {recipe.labels.slice(0, 3).map((label) => (
                                <Badge key={label} variant="secondary" className="text-[10px]">
                                    {label}
                                </Badge>
                            ))}
                            {recipe.labels.length > 3 ? (
                                <Badge variant="outline" className="text-[10px]">
                                    +{recipe.labels.length - 3}
                                </Badge>
                            ) : null}
                        </div>
                    ) : null}
                </CardContent>
            </Card>
        </Link>
    );
}

